import { useForm } from "@tanstack/react-form";
import { Loader2, Upload, Link as LinkIcon, Copy, Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useTestConnection, useUploadLogo, useUploadReturnInstruction, useRemoveReturnInstruction } from "@/hooks/use-stores";

interface StoreFormValues {
  name: string;
  url: string;
  consumerKey: string;
  consumerSecret: string;
  slug: string;
  primaryColor: string;
  returnsEnabled: boolean;
}

interface StoreFormProps {
  storeId?: string;
  initialValues?: Partial<StoreFormValues> & { logoUrl?: string; returnInstructionUrl?: string };
  onSubmit: (values: StoreFormValues) => Promise<void>;
  loading: boolean;
  submitLabel: string;
}

export function StoreForm({ storeId, initialValues, onSubmit, loading, submitLabel }: StoreFormProps) {
  const form = useForm({
    defaultValues: {
      name: initialValues?.name ?? "",
      url: initialValues?.url ?? "",
      consumerKey: initialValues?.consumerKey ?? "",
      consumerSecret: initialValues?.consumerSecret ?? "",
      slug: initialValues?.slug ?? "",
      primaryColor: initialValues?.primaryColor ?? "#4F46E5",
      returnsEnabled: initialValues?.returnsEnabled ?? false,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
  });

  const testConnection = useTestConnection();
  const uploadLogo = useUploadLogo();
  const uploadInstruction = useUploadReturnInstruction();
  const removeInstruction = useRemoveReturnInstruction();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const instructionInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [instructionDragOver, setInstructionDragOver] = useState(false);

  async function handleInstructionFile(file: File | undefined) {
    if (!file || !storeId) return;
    try {
      await uploadInstruction.mutateAsync({ id: storeId, file });
      toast.success("Slika s uputstvom uploadovana");
    } catch (err: any) {
      toast.error(err.message || "Greška pri uploadu");
    }
  }
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  async function handleTestConnection() {
    try {
      await testConnection.mutateAsync({
        url: form.getFieldValue("url"),
        consumerKey: form.getFieldValue("consumerKey"),
        consumerSecret: form.getFieldValue("consumerSecret"),
      });
      toast.success("Konekcija uspješna!");
    } catch (e: any) {
      toast.error(e.message || "Konekcija nije uspjela");
    }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); form.handleSubmit(); }} className="flex flex-col gap-4 w-full">
      <div className="ring-1 ring-foreground/10 w-full">
        <div className="border-b bg-muted/50 px-4 py-2">
          <span className="text-xs font-medium">WooCommerce konekcija</span>
        </div>
        <div className="p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="name" className="text-xs">Naziv trgovine</Label>
              <form.Field name="name">
                {(field) => (
                  <Input
                    id="name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="Moja trgovina"
                    required
                  />
                )}
              </form.Field>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="url" className="text-xs">WooCommerce URL</Label>
              <form.Field name="url">
                {(field) => (
                  <Input
                    id="url"
                    type="url"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="https://mojatrgovine.ba"
                    required
                  />
                )}
              </form.Field>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="consumerKey" className="text-xs">Consumer Key</Label>
              <form.Field name="consumerKey">
                {(field) => (
                  <div className="relative">
                    <Input
                      id="consumerKey"
                      type={showKey ? "text" : "password"}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="ck_..."
                      className="pr-9"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                )}
              </form.Field>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="consumerSecret" className="text-xs">Consumer Secret</Label>
              <form.Field name="consumerSecret">
                {(field) => (
                  <div className="relative">
                    <Input
                      id="consumerSecret"
                      type={showSecret ? "text" : "password"}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="cs_..."
                      className="pr-9"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                )}
              </form.Field>
            </div>

            <form.Subscribe selector={(state) => ({
              url: state.values.url,
              consumerKey: state.values.consumerKey,
              consumerSecret: state.values.consumerSecret,
            })}>
              {({ url, consumerKey, consumerSecret }) => (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={handleTestConnection}
                  disabled={testConnection.isPending || !url || !consumerKey || !consumerSecret}
                >
                  {testConnection.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Testiraj konekciju
                </Button>
              )}
            </form.Subscribe>
          </div>
        </div>
      </div>

      {storeId && (
        <div className="ring-1 ring-foreground/10 w-full">
          <div className="border-b bg-muted/50 px-4 py-2">
            <span className="text-xs font-medium">Povrat proizvoda</span>
          </div>
          <div className="p-4 flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="slug" className="text-xs">Slug (URL identifikator)</Label>
              <form.Field name="slug">
                {(field) => (
                  <Input
                    id="slug"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                    placeholder="moja-trgovina"
                  />
                )}
              </form.Field>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Logo</Label>
              <div
                className={`relative group rounded border-2 border-dashed cursor-pointer transition-colors ${
                  dragOver ? "border-primary bg-primary/5" : "border-foreground/10 hover:border-foreground/20"
                }`}
                onClick={() => logoInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={async (e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (!file || !storeId) return;
                  try {
                    await uploadLogo.mutateAsync({ id: storeId, file });
                    toast.success("Logo uploadovan");
                  } catch (err: any) {
                    toast.error(err.message || "Greška pri uploadu");
                  }
                }}
              >
                {initialValues?.logoUrl ? (
                  <div className="relative">
                    <img src={initialValues.logoUrl} alt="Logo" className="h-20 w-full rounded object-contain p-3" />
                    <div className="absolute inset-0 rounded bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                      {uploadLogo.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <>
                          <Upload className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Promijeni logo</span>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 gap-1.5">
                    {uploadLogo.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <Upload className="h-5 w-5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Klikni ili prevuci sliku</span>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file || !storeId) return;
                    try {
                      await uploadLogo.mutateAsync({ id: storeId, file });
                      toast.success("Logo uploadovan");
                    } catch (err: any) {
                      toast.error(err.message || "Greška pri uploadu");
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Slika s uputstvom (opcionalno)</Label>
                {initialValues?.returnInstructionUrl && storeId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-xs text-muted-foreground hover:text-destructive"
                    disabled={removeInstruction.isPending}
                    onClick={async () => {
                      try {
                        await removeInstruction.mutateAsync({ id: storeId });
                        toast.success("Slika uklonjena");
                      } catch (err: any) {
                        toast.error(err.message || "Greška pri uklanjanju");
                      }
                    }}
                  >
                    {removeInstruction.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                    <span className="ml-1">Ukloni</span>
                  </Button>
                )}
              </div>
              <div
                className={`relative group rounded border-2 border-dashed cursor-pointer transition-colors ${
                  instructionDragOver ? "border-primary bg-primary/5" : "border-foreground/10 hover:border-foreground/20"
                }`}
                onClick={() => instructionInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setInstructionDragOver(true); }}
                onDragLeave={() => setInstructionDragOver(false)}
                onDrop={async (e) => {
                  e.preventDefault();
                  setInstructionDragOver(false);
                  await handleInstructionFile(e.dataTransfer.files[0]);
                }}
              >
                {initialValues?.returnInstructionUrl ? (
                  <div className="relative">
                    <img src={initialValues.returnInstructionUrl} alt="Uputstvo" className="max-h-40 w-full rounded object-contain p-3" />
                    <div className="absolute inset-0 rounded bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                      {uploadInstruction.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <>
                          <Upload className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Promijeni sliku</span>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 gap-1.5">
                    {uploadInstruction.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <Upload className="h-5 w-5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Klikni ili prevuci sliku</span>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={instructionInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => { await handleInstructionFile(e.target.files?.[0]); }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Prikazuje se kupcu na dnu forme za povrat, iznad dugmeta „Pošalji zahtjev".
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="primaryColor" className="text-xs">Primarna boja</Label>
              <div className="flex gap-2 items-center">
                <form.Field name="primaryColor">
                  {(field) => (
                    <>
                      <input
                        type="color"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        className="h-8 w-8 rounded border cursor-pointer"
                      />
                      <Input
                        id="primaryColor"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="#4F46E5"
                        className="flex-1"
                      />
                    </>
                  )}
                </form.Field>
              </div>
            </div>

            <form.Field name="returnsEnabled">
              {(field) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="returnsEnabled"
                    checked={field.state.value}
                    onCheckedChange={(checked) => field.handleChange(checked === true)}
                  />
                  <Label htmlFor="returnsEnabled" className="text-xs cursor-pointer">Omogući formu za povrat</Label>
                </div>
              )}
            </form.Field>

            <form.Subscribe selector={(state) => state.values.slug}>
              {(slug) =>
                slug ? (
                  <div className="flex items-center gap-2 rounded bg-muted/50 px-3 py-2">
                    <LinkIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground truncate">
                      {window.location.origin}/return/{slug}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/return/${slug}`);
                        toast.success("Link kopiran");
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                ) : null
              }
            </form.Subscribe>
          </div>
        </div>
      )}

      <Button type="submit" size="sm" className="self-start" disabled={loading}>
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {submitLabel}
      </Button>
    </form>
  );
}
